/**
 * ServiceDetailFloater — read-only side panel rendered next to the Quick
 * Message modal when an admin clicks the Planning Center widget's "Quick
 * Message" button. Lets the admin see the upcoming service's team members,
 * song list, and keys while composing.
 *
 * Mount lifecycle: rendered by App.js as the QuickComposeModal's `rightSidebar`
 * prop. Animation classes on the slot (in QuickComposeModal.jsx) handle the
 * subtle slide-in-from-right + fade-in with a small delay so the floater
 * appears to "expand out" from the modal after it opens.
 *
 * Snapshot semantics: receives the plan object captured at click-time in
 * PlanningCenterWidget. If the user cycles to a different service while the
 * modal is open, the floater intentionally shows the plan that was active
 * when they clicked Quick Message — matching Apple Calendar–style "open with
 * the moment you triggered" UX.
 */

import React from 'react';
import { Music, ExternalLink, CheckCircle2, XCircle, Clock, Users } from 'lucide-react';

function formatName(fullName) {
  const parts = (fullName || '').trim().split(' ');
  if (parts.length < 2) return fullName;
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

function StatusIcon({ status }) {
  if (status === 'confirmed') return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />;
  if (status === 'declined') return <XCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />;
  return <Clock className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />;
}

export default function ServiceDetailFloater({ plan }) {
  if (!plan) return null;

  const team = plan.team_members || [];
  const songs = plan.songs || [];
  const songCount = plan.song_count || songs.length;

  return (
    <div
      className="w-80 max-h-[80vh] bg-card rounded-2xl shadow-2xl shadow-black/20 border border-slate-200/80 dark:border-white/10 flex flex-col overflow-hidden backdrop-blur-md transition-all duration-300 ease-in-out"
      data-testid="service-detail-floater"
    >
      {/* Header */}
      <div className="px-4 pt-4 pb-3 flex items-start gap-2.5 border-b border-slate-100 dark:border-white/[0.08] flex-shrink-0">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-md shadow-teal-500/25 flex-shrink-0">
          <Music className="w-4 h-4 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
            {plan.group_name || 'Upcoming Service'}
          </h3>
          {(plan.date || plan.time) && (
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
              {plan.date}{plan.time ? ` \u00B7 ${plan.time}` : ''}
            </p>
          )}
        </div>
      </div>

      {/* Body — scrolls when content overflows */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {/* Team */}
        <section>
          <div className="flex items-center gap-1.5 mb-2">
            <Users className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
            <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-400 dark:text-slate-500">
              Team {team.length > 0 && `(${team.length})`}
            </span>
          </div>
          {team.length > 0 ? (
            <div className="grid grid-cols-2 gap-1.5">
              {team.map((m, i) => (
                <div
                  key={i}
                  className="flex items-center gap-1.5 bg-slate-50/80 dark:bg-white/[0.03] rounded-lg px-2 py-1"
                  title={`${m.name} \u00B7 ${m.status || 'unconfirmed'}`}
                >
                  <StatusIcon status={m.status} />
                  <span className="text-xs text-slate-700 dark:text-slate-200 truncate">
                    {formatName(m.name)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-400 dark:text-slate-500 italic">No team assigned yet</p>
          )}
        </section>

        {/* Songs */}
        <section>
          <div className="flex items-center gap-1.5 mb-2">
            <Music className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
            <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-400 dark:text-slate-500">
              Songs {songCount > 0 && `(${songCount})`}
            </span>
          </div>
          {songs.length > 0 ? (
            <div className="space-y-1.5">
              {songs.map((s, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between gap-2 bg-slate-50/80 dark:bg-white/[0.03] rounded-lg px-2.5 py-1.5"
                >
                  <span className="text-xs text-slate-700 dark:text-slate-200 truncate" title={s.title}>
                    {s.title}
                  </span>
                  {s.key && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-white dark:bg-white/10 text-teal-600 dark:text-teal-400 flex-shrink-0">
                      {s.key}
                    </span>
                  )}
                </div>
              ))}
              {songCount > songs.length && (
                <p className="text-[11px] text-slate-400 dark:text-slate-500 pl-1">
                  +{songCount - songs.length} more
                </p>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-400 dark:text-slate-500 italic">No songs yet</p>
          )}
        </section>
      </div>

      {/* Footer link */}
      {plan.plan_url && (
        <a
          href={plan.plan_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-between gap-2 px-4 py-2.5 border-t border-slate-100 dark:border-white/[0.08] text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-slate-50/60 dark:hover:bg-white/[0.02] transition-colors flex-shrink-0"
          data-testid="service-detail-floater-plan-link"
        >
          <span>View full plan in Planning Center</span>
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
    </div>
  );
}
